<template>
    <div :class="isMobile ? 'flex flex-col gap-3 mt-3' : 'flex flex-row justify-between items-center gap-3'">
      <!-- Date Picker Group -->
      <div class="flex items-center gap-2 w-full sm:w-auto">
        <DatePicker v-model="datesComputed" selectionMode="range" showIcon :showOnFocus="true" class="w-full sm:w-auto" />
        <i v-if="!isMobile" class="pi pi-info-circle text-gray-400 cursor-help"
          v-tooltip.bottom="'Daily statistics are only available when viewing a single month by clicking on the row.'" 
        />
      </div>
  
      <!-- Month Selection - Only show on desktop -->
      <div v-if="!isMobile" class="flex flex-wrap gap-1 justify-center">
        <Button v-for="(month, index) in months" :key="index" :label="month.label"
          @click="selectedMonth(index, month.start, month.end)"
          :severity="selectedMonthIndexComputed === index ? 'primary' : 'secondary'" 
          class="h-7" 
        />
      </div>
      
      <!-- INSERT_YOUR_CODE -->
      <div v-if="isAdmin && !isFinance" class="flex items-center gap-2">
        <span class="text-xs text-gray-600">Publisher split</span>
        <ToggleSwitch v-model="props.publisherSplit" @change="handlePublisherSplitChange">
          <template #handle="{ checked }">
            <i :class="['!text-xs pi', { 'pi-check': checked, 'pi-times': !checked }]" />
          </template>
        </ToggleSwitch>
      </div>
      <!-- Controls -->
      <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <SplitButton v-if="isAdmin" 
          label="Export" 
          icon="pi pi-file-excel" 
          severity="info" 
          :loading="isExporting"
          @click="handleExport" 
          :model="exportOptions"
          class="p-button-sm w-full sm:w-auto"
        />
        <Select v-model="selectedDatePresetComputed" placeholder="Select preset" :options="datePresets"
          optionLabel="name" @change="handleDatePresetChange" class="w-full sm:w-auto" 
        />
      </div>
    </div>
  </template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue';
import { downloadCSV } from '@/utils/csv';
import { useToast } from 'primevue/usetoast';
import ExportService from '@/services/ExportService';
import { getMonthAndYear } from '@/utils/dateHelper';
import { useStore } from 'vuex';
const props = defineProps({
  dates: {
    type: Array,
    required: true
  },
  months: {
    type: Array,
    required: true
  },
  selectedMonthIndex: {
    type: Number,
    required: true
  },
  selectedDatePreset: {
    type: Object,
    default: null
  },
  datePresets: {
    type: Array,
    required: true
  },
  isAdmin: {
    type: Boolean,
    default: false
  },
  isExporting: {
    type: Boolean,
    default: false
  },
  canExport: {
    type: Boolean,
    default: false
  },
  isMobile: {
    type: Boolean,
    required: true
  },
  publisherSplit: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:dates', 'update:selectedMonthIndex', 'update:selectedDatePreset', 'monthSelect', 'datePresetChange', 'update:publisherSplit']);

const toast = useToast();
const isExporting = ref(false);
const store = useStore();

const isFinance = computed(() => store.getters.isFinance);
const handleExport = async () => {
    if (!props.dates[0] || !props.dates[1]) {
        toast.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Please select a date range first',
            life: 3000
        });
        return;
    }

    isExporting.value = true;
    try {
        const response = await ExportService.getSpaceAdvertisersEarnings(
            props.dates[0]
        );
        const { monthName, year } = getMonthAndYear(props.dates[0]);
        // Generate filename with date range
        const filename = `Advertiser Earnings - ${monthName}${year}.csv`;

        // Use the existing CSV utility
        downloadCSV(response.data, filename);

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Export completed successfully',
            life: 3000
        });
    } catch (error) {
        console.error('Export failed:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to export data',
            life: 3000
        });
    } finally {
        isExporting.value = false;
    }
};

const handleExportByGeo = async () => {
    console.log('Export by Geo clicked');
    if (!props.dates[0] || !props.dates[1]) {
        toast.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Please select a date range first',
            life: 3000
        });
        return;
    }

    isExporting.value = true;
    try {
      const response = await ExportService.getEarningsByGeo(
            props.dates[0]
        );
        const { monthName, year } = getMonthAndYear(props.dates[0]);
        // Generate filename with date range
        const filename = `Earnings by Geo - ${monthName}${year}.csv`;

        // Use the existing CSV utility
        downloadCSV(response.data, filename);

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Export by Geo completed successfully',
            life: 3000
        });
    } catch (error) {
        console.error('Export failed:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to export data',
            life: 3000
        });
    } finally {
        isExporting.value = false;
    }

}

const handleExportByPublisher = async () => {
    console.log('Export by Publisher clicked');
    if (!props.dates[0] || !props.dates[1]) {
        toast.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Please select a date range first',
            life: 3000
        });
        return;
    }

    isExporting.value = true;
    try {
      const response = await ExportService.getEarningsByAdvertiserThenByPublisher(
            props.dates[0]
        );
        const { monthName, year } = getMonthAndYear(props.dates[0]);
        // Generate filename with date range
        const filename = `Advertiser Earnings By Publisher - ${monthName}${year}.csv`;

        // Use the existing CSV utility
        downloadCSV(response.data, filename);

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Export by Publisher completed successfully',
            life: 3000
        });
    } catch (error) {
        console.error('Export failed:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to export data',
            life: 3000
        });
    } finally {
        isExporting.value = false;
    }

}
const exportOptions = [
  {
    label: 'Export by Geo',
    icon: 'pi pi-globe',
    command: () => {
       handleExportByGeo();
    }
  },
  {
    label: 'Export by publisher',
    icon: 'pi pi-user',
    command: () => {
       handleExportByPublisher();
    }
  }
];

// Computed properties for v-model
const datesComputed = computed({
  get: () => props.dates,
  set: (value) => emit('update:dates', value)
});

const selectedMonthIndexComputed = computed({
  get: () => props.selectedMonthIndex,
  set: (value) => emit('update:selectedMonthIndex', value)
});

const selectedDatePresetComputed = computed({
  get: () => props.selectedDatePreset,
  set: (value) => emit('update:selectedDatePreset', value)
});

const selectedMonth = (index, start, end) => {
  emit('monthSelect', { index, start, end });
};

const handleDatePresetChange = () => {
  emit('datePresetChange');
};

const handlePublisherSplitChange = () => {
  emit('update:publisherSplit', !props.publisherSplit);
};
</script>

<style scoped>
:deep(.p-calendar) {
  width: 100%;
}

:deep(.p-calendar .p-inputtext) {
  width: 100%;
}

:deep(.p-dropdown) {
  width: 100%;
}

@media (min-width: 640px) {
  :deep(.p-calendar),
  :deep(.p-calendar .p-inputtext) {
    width: auto;
  }
  
  :deep(.p-dropdown) {
    min-width: 200px;
  }
}
</style> 